// @erp/ui/link-button — a router Link that LOOKS like a button (2026-09-18,
// audit UX-13 (f)).
//
// WHY IT EXISTS. Thirty-two screens wrote `<Link to="…"><Button>Add title</Button></Link>`
// — a header verb that navigates. The anchor and the button are BOTH
// interactive, so a keyboard user met two tab stops for one action (the audit
// walked "Import" → button#btn-books-import "Import"), axe flagged every one
// as nested-interactive, and a screen reader announced "link, button, Import".
// Button's own `href` form is a plain anchor with no router — right for a
// served PDF, wrong for a same-app route, which must preload and stay in the
// SPA. This is the missing third shape: ONE element, the router's <Link>, in
// buttonClasses — the recipe NotFoundCard and EmptyState already wear.
//
// The rule now reads: a same-app route that must look like a button is a
// LinkButton; a served file or external page is Button `href`; a click that
// does something is Button. Never a Button inside a Link.
import { Link } from '@tanstack/solid-router';
import type { JSX } from 'solid-js';
import { buttonClasses, type ButtonSize, type ButtonTreatment, type ButtonVariant } from '../button/index.js';

export interface LinkButtonProps {
  /** The route, as the router's Link takes it (`/library/patrons/new`, `/finance/invoices/$invoiceId`). */
  to: string;
  /** Path params for a dynamic route (`{ invoiceId: i().id }`). */
  params?: Record<string, string | number>;
  /** Search params to carry (`{ invoice: i().invoiceNo }`). */
  search?: Record<string, unknown>;
  /** Router preloading; default `intent` (on hover/focus) — the reason this is a Link and not an anchor. */
  preload?: 'intent' | 'render' | 'viewport' | false;
  /** Replace the history entry instead of pushing one. */
  replace?: boolean;
  /**
   * Inert: the anchor keeps its place and its label but has NO href (an <a>
   * without href is neither focusable nor a link), wears daisyUI's
   * `btn-disabled` rest state and says why through `aria-disabled` — the same
   * contract as Button's own `href` form. The router's Link does the
   * withholding (its `disabled`), so no click can navigate or preload.
   */
  disabled?: boolean;
  /** EMPHASIS — the same vocabulary as Button: `primary` for the one verb the view is for, `secondary`, `ghost`, `outline`, `soft` (default), `error`. */
  variant?: ButtonVariant;
  /** `sm` (default, 32px) or `md` (40px), as Button. */
  size?: ButtonSize;
  /** Fill (default) or outline, as Button. */
  treatment?: ButtonTreatment;
  /** Automation id on the anchor — the ONE element, so the id a test read off the inner Button is now here (`btn-books-add`). */
  id?: string;
  /** Layout classes only (`mt-4`, `w-full`); the skin is the recipe's. */
  class?: string;
  /** A tooltip, when the label alone is short. */
  title?: string;
  /** An icon-only link's name; a labelled one needs none. */
  'aria-label'?: string;
  /** The label — usually an icon + text, exactly as a Button's children. */
  children: JSX.Element;
}

/**
 * A same-app navigation dressed as a button: "Add title" in a list header,
 * "Open register" on a detail card, "Sign in" on a public page. One anchor,
 * one tab stop, the router's preloading. For a served file use Button `href`;
 * for a click that acts, Button.
 */
export function LinkButton(props: LinkButtonProps) {
  const variant = () => props.variant ?? 'soft';
  return (
    <Link
      id={props.id}
      to={props.to}
      params={props.params}
      search={props.search}
      preload={props.preload ?? 'intent'}
      replace={props.replace}
      disabled={props.disabled}
      title={props.title}
      aria-label={props['aria-label']}
      class={`${buttonClasses({ variant: variant(), size: props.size, treatment: props.treatment, class: props.class })} ${props.disabled ? 'btn-disabled' : ''}`}
      data-variant={variant()}
    >
      {props.children}
    </Link>
  );
}
